import { useTranslation } from 'react-i18next';
import type { UserResponse } from '@/shared/api';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components';
import { fullName } from './userStatus';
import { useInvitationLink } from './useInvitationLink';

export function UserInvitationLinkDialog({
  user,
  open,
  onClose,
}: {
  user: UserResponse;
  open: boolean;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation('invitations');
  const link = useInvitationLink(user.id, open);
  const close = () => {
    link.clear();
    onClose();
  };
  const ready = link.value && !link.expired && !link.pending;
  return (
    <Dialog open={open} onOpenChange={(next) => !next && close()}>
      <DialogContent>
        <DialogHeader className="pr-14">
          <DialogTitle>{t('title')}</DialogTitle>
          <DialogDescription className="break-words">
            {t('description', { name: fullName(user), email: user.email })}
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4 text-sm">
          <p className="text-ink-soft">{t('initial')}</p>
          <p className="border-amber/30 bg-amber-wash rounded-control border p-3">{t('warning')}</p>
          {!link.online && (
            <p role="status" className="text-sealwax">
              {t('offline')}
            </p>
          )}
          {link.pending && <p role="status">{t('generating')}</p>}
          {link.notice && (
            <p role={link.notice === 'copied' ? 'status' : 'alert'}>{t(link.notice)}</p>
          )}
          {link.expired && <p role="status">{t('expired')}</p>}
          {link.value && (
            <p className="text-ink-soft">
              {t('expires', {
                date: new Intl.DateTimeFormat(i18n.language, {
                  dateStyle: 'medium',
                  timeStyle: 'short',
                }).format(new Date(link.value.expiresAt)),
              })}
            </p>
          )}
          {ready && (
            <label className="block space-y-2">
              <span className="font-semibold">{t('link')}</span>
              <textarea
                readOnly
                spellCheck={false}
                rows={4}
                value={link.value!.url}
                className="border-line bg-card-alt rounded-control w-full min-w-0 resize-none border p-3 text-sm"
              />
            </label>
          )}
          <p className="text-ink-soft">{t('transient')}</p>
        </DialogBody>
        <DialogFooter className="grid grid-cols-2 items-stretch">
          <Button type="button" size="lg" variant="secondary" onClick={close}>
            {t('close')}
          </Button>
          {ready ? (
            <Button type="button" size="lg" variant="brand" onClick={() => void link.copy()}>
              {t('copy')}
            </Button>
          ) : (
            <Button
              data-invitation-generate
              type="button"
              size="lg"
              variant="brand"
              className="whitespace-normal"
              loading={link.pending}
              disabled={!link.online}
              onClick={link.generate}
            >
              {t(link.pending ? 'generating' : link.attempted ? 'regenerate' : 'generate')}
            </Button>
          )}
          {ready && (
            <Button
              type="button"
              size="lg"
              className="col-span-2"
              disabled={!link.online}
              onClick={link.generate}
            >
              {t('regenerate')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
